'use client';

import React, { useEffect, useRef, useState } from 'react';

/**
 * Animates the numeric part of a stat (e.g. "150+", "7 Day", "1,300") counting
 * up from zero the first time it scrolls into view. Non-numeric prefixes/suffixes
 * are preserved, and values without a number (e.g. "Growing") render unchanged.
 * Respects prefers-reduced-motion.
 */
export default function CountUp({
  value,
  className,
  style,
}: {
  value: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const match = value.match(/^(\D*)(\d[\d,]*)(.*)$/);
  const target = match ? parseInt(match[2].replace(/,/g, ''), 10) : null;
  const [n, setN] = useState(0);

  useEffect(() => {
    if (target === null) return;
    const el = ref.current;
    if (!el) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setN(target);
      return;
    }

    let done = false;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !done) {
            done = true;
            const duration = 1300;
            const startTime = performance.now();
            const step = (now: number) => {
              const p = Math.min(1, (now - startTime) / duration);
              const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
              setN(Math.round(eased * target));
              if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target]);

  if (target === null || !match) {
    return (
      <span className={className} style={style}>
        {value}
      </span>
    );
  }

  return (
    <span ref={ref} className={className} style={style}>
      {match[1]}
      {n.toLocaleString()}
      {match[3]}
    </span>
  );
}
